// External dependencies
import { Input } from "@/components/ui/input";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { useActiveMenu } from "@/hooks/use-active-menu";
import { authStore } from "@/providers/authStore";
import getPharmacyID from "@/utils/pharmacy_code";
import { Link, useNavigate } from "@tanstack/react-router";
import { ChevronsUpDown, HelpCircle, LogOut, Search, Settings } from "lucide-react";
import * as React from "react";
import { useEffect, useState } from "react";
import { NavFooter } from "./nav-footer";
import { NavMain } from "./nav-main";
import { sidebarMenus } from "./sidebar-menus";

/**
 * AppSidebar Component
 *
 * Main application sidebar with navigation sections for the dashboard.
 * Includes app logo/header, main navigation, workspace selection,
 * secondary links, and user profile.
 */

enum OS {
  Windows = "Windows",
  Macintosh = "Macintosh",
  Linux = "Linux",
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { state } = useSidebar();
  const navigate = useNavigate();

  React.useEffect(() => {
    localStorage.setItem("sidebar-open", (state === "expanded").toString());
  }, [state]);

  // Footer icon click handlers
  const handleSettings = () => {
    navigate({to: "/company/settings"});
  };
  const handleLogout = () => {
    localStorage.clear();
    authStore.clear();
    navigate({ to: "/login" });
  };

  // Get active state for navs
  const { activeItems: mainActive } = useActiveMenu(sidebarMenus.navMain);
  const { activeItems: belowViewsActive } = useActiveMenu(sidebarMenus.navBelowViews);
  const [os, setOs] = useState(OS.Windows);
  const placeholdres = [
    "Search for medicines",
    "Search for invoices",
    "Search for reports",
  ];
  const [placeHolderIdx, setPlaceHolderIdx] = useState(0);
  const [placeholder, setPlaceholder] = useState(placeholdres[placeHolderIdx]);

  useEffect(() => {
    const ua = window.navigator.userAgent;
    if (ua.includes("Macintosh")) {
      setOs(OS.Macintosh);
    }
    const placeholderRotatior = setInterval(() => {
      //weird issues with useState closures and use effect
      //https://stackoverflow.com/questions/53024496/state-not-updating-when-using-react-state-hook-within-setinterval
      setPlaceHolderIdx((oldIdx) => (oldIdx + 1) % placeholdres.length);
    }, 2500);
    return () => {
      clearInterval(placeholderRotatior);
    };
  }, []);

  return (
    <Sidebar
      variant="inset"
      collapsible="icon"
      className="sidebar-text-tight"
      {...props}
      aria-label="Main navigation"
    >
      {/* Only show header, quick actions, search, navigation, and footer when expanded */}
      {state === "expanded" && (
        <>
          <SidebarHeader className="app-header-divider h-14 pt-2 mt-[0.5px]">
            <div className="flex items-center justify-between w-full px-2">
            <button
              onClick={() => {
                window.location.href = '/select-access'
              }}
              className="flex items-center hover:opacity-80 transition-opacity min-w-0 flex-1"
              aria-label="Switch pharmacy context"
            >
              <div
                className="flex aspect-square size-8 items-center justify-center rounded-md border border-border p-1.5 flex-shrink-0"
                aria-hidden="true"
              >
                <img
                  src="/icon.png"
                  alt="Neulink Logo"
                  width={24}
                  height={24}
                  className="object-contain"
                />
              </div>
              {state === "expanded" && (
                <div className="px-2 text-left text-md leading-tight min-w-0">
                  <span className="font-semibold whitespace-normal block">{
                    localStorage.getItem("companyName")
                  }
                  <ChevronsUpDown className="size-3 inline ml-1"></ChevronsUpDown>
                  </span>
                </div>
              )}
            </button>
              {state === "expanded" && (
                <div className="flex-shrink-0">
                  <SidebarTrigger />
                </div>
              )}
            </div>
          </SidebarHeader>
          <div 
            role="button"
            onMouseDown={(e) => {
              if (getPharmacyID() === "") {
                e.preventDefault();
                return;
              }
              const commandEvent = new Event("open-command-menu");
              document.dispatchEvent(commandEvent);
            }}
            className="flex gap-1 px-3 pt-2 pb-1">
            <button className="flex-1 flex items-center gap-1 border border-border rounded-md px-2 h-9 text-xs font-medium text-foreground shadow-xs min-w-0">
              <div className="flex items-center gap-1">
                <Search size={15} className="text-muted-foreground mr-2" />
                <Input
              id="MainSearch"
              placeholder="Search"
              className="hidden md:block h-8 min-w-lg border-0 text-foreground pl-0"
            />
          </div>
          <div className="h-6 w-[1px] bg-border mx-2" />
          <span className="border border-input text-sm rounded-md flex gap-2 items-center px-2 my-2">
            {os === OS.Macintosh ? (
              <span className="flex gap-1 items-center text-xs font-semibold text-muted-foreground">
                <kbd className="text-sm">⌘</kbd> + <kbd>K</kbd>
              </span>
            ) : (
              <span className="flex gap-1 items-center text-xs font-semibold text-muted-foreground">
                <kbd>Ctrl</kbd> + <kbd>K</kbd>
              </span>
            )}
          </span>
          </button>
        </div>
          <SidebarContent>
            <div className="">
              <NavMain items={sidebarMenus.navMain} />
              <div className="flex items-center gap-1 text-xs text-muted-foreground font-medium mt-8 pl-2">
                <sidebarMenus.navViews.icon
                  className="w-3 h-3"
                  aria-hidden="true"
                />
                <span>Views</span>
              </div>
              <NavMain items={sidebarMenus.navBelowViews} />
            </div>
          </SidebarContent>
          <SidebarFooter>
            {/* <NavMain items={sidebarMenus.navSecondary} /> */}
            <div className="py-2">
              <NavFooter />
            </div>
          </SidebarFooter>
        </>
      )}
      {/* Collapsed state: logo at top, toggle button below, nav icons in middle, footer icons at bottom */}
      {state === "collapsed" && (
        <div className="flex flex-col h-full justify-between">
          {/* Top: logo */}
          <div className="flex flex-col items-center pt-4">
            <Link
              to="/companny/dashboard"
              className="flex items-center"
              aria-label="Go to dashboard home"
            >
              <div
                className="flex aspect-square size-8 items-center justify-center rounded-md border border-border p-1.5"
                aria-hidden="true"
              >
                <img
                  src="/icon.png"
                  alt="Neulink Logo"
                  width={24}
                  height={24}
                  className="object-contain"
                />
              </div>
            </Link>
            {/* Toggle button centered below logo */}
            <div className="mt-3 flex justify-center">
              <SidebarTrigger />
            </div>
          </div>
          {/* Middle: nav icons */}
          <nav className="flex flex-col items-center gap-2">
            {[...mainActive, ...belowViewsActive].map((item) => (
              <div key={item.url} className="flex flex-col items-center">
                <button
                  className={`flex items-center justify-center w-10 h-10 rounded-md ${item.isActive ? "bg-gray-100" : ""} hover:bg-muted focus:bg-muted`}
                  title={item.title}
                  aria-label={item.title}
                  tabIndex={0}
                  onClick={() => {
                    window.location.href = item.url;
                  }}
                >
                  <item.icon className="w-4 h-4" aria-hidden="true" />
                </button>
              </div>
            ))}
          </nav>
          {/* Bottom: footer icons */}
          <div className="flex flex-col items-center gap-2 pb-4">
            <button
              className="flex items-center justify-center w-10 h-10 rounded-md hover:bg-muted focus:bg-muted"
              title="Settings"
              aria-label="Settings"
              tabIndex={0}
              onClick={handleSettings}
            >
              <Settings className="w-4 h-4" aria-hidden="true" />
            </button>
            <button
              className="flex items-center justify-center w-10 h-10 rounded-md hover:bg-muted focus:bg-muted"
              title="Help and first steps"
              aria-label="Help and first steps"
              tabIndex={0}
            >
              <HelpCircle className="w-4 h-4" aria-hidden="true" />
            </button>
            <button
              className="flex items-center justify-center w-10 h-10 rounded-md hover:bg-muted focus:bg-muted"
              title="Logout"
              aria-label="Logout"
              tabIndex={0}
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </Sidebar>
  );
}